import { OfflineButton } from '@/components/studio/OfflineNotice';
import { useStudioOnline } from '@/lib/tool-availability';
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode, type SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Camera, Check, Loader2, Lock, Palette, ScanFace, ShieldCheck, Sparkles, UserPlus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/lib/analytics';
import {
  StorybookApiError,
  createStorybook,
  rememberStorybook,
  saveStorybookToken,
  type CreateStorybookRequest,
  type StorybookAppearance,
  type StorybookCompanion,
  type StorybookConfig,
  type StorybookGender,
  type StorybookLanguage,
} from '@/lib/storybook-api';
import {
  EYE_COLOR_SWATCHES,
  HAIR_COLOR_SWATCHES,
  SKIN_TONE_SWATCHES,
  appearanceSlug,
  formatStorybookList,
  isBaldHairStyle,
  storybookAgeBand,
  storybookOptionLabel,
  storybookUiLanguage,
} from '@/lib/storybook-utils';
import {
  bestTaggedFace,
  buildPhotoPayload,
  hasSentFace,
  payloadFaceBox,
  photoIsUsed,
  storybookTagLimits,
  untagPerson,
  type StorybookPhotoItem,
  type StorybookPhotoPayload,
  type StorybookTaggablePerson,
} from '@/lib/storybook-tagging';
import {
  STORYBOOK_COLOUR_FIELDS,
  estimateStorybookFaceColours,
  photoSuggestion,
  storybookColourPerson,
  type StorybookColourField,
  type StorybookPhotoRead,
  type StorybookPhotoSuggestion,
} from '@/lib/storybook-colour';
import { StorybookChoiceGroup, type StorybookChoice } from './StorybookChoiceGroup';
import { StorybookCharacterCard } from './StorybookCharacterCard';
import { StorybookPhotoTagger } from './StorybookPhotoTagger';
import { StorybookAvatarPicker } from './StorybookAvatarPicker';
import { useFaceLockCrew } from '@/components/facelock/useFaceLockCrew';
import SiteMarkSwitch from '@/components/pay/SiteMarkSwitch';
import { keepSiteMark } from '@/lib/site-mark';

// ---------------------------------------------------------------------------
// Form model
// ---------------------------------------------------------------------------

type AppearanceField = 'skinTone' | 'hairColor' | 'hairStyle' | 'eyeColor';

interface Look {
  skinTone: string;
  hairColor: string;
  hairStyle: string;
  eyeColor: string;
  glasses: boolean;
  details: string;
}

/** A companion who joins the story (up to config.cast.maxCompanions). */
interface CompanionForm extends Look {
  /** Stable while the form is edited: ids, errors and photo tags follow it when the one before is removed. */
  key: string;
  name: string;
  relation: string;
  /** Child relations only (big sister, big brother, friend); '' = not given. */
  age: string;
  /** A friend only; '' = not given. */
  gender: StorybookGender | '';
}

interface BuilderForm extends Look {
  childName: string;
  age: string;
  gender: StorybookGender | '';
  companions: CompanionForm[];
  /** The photo step (docs/storybook-identity-lock.md §2.2); only tagged photos are ever sent. */
  photos: StorybookPhotoItem[];
  /** Grown-up companion key -> the signed-in parent's FaceLock avatar drawing them (never also from photos). */
  avatars: Record<string, string>;
  themeId: string;
  lessonId: string;
  interests: string;
  sidekick: string;
  artStyleId: string;
  language: StorybookLanguage;
  dedication: string;
  email: string;
  consent: boolean;
  /** The separate photo consent, shown (and required) only when photos will be sent. */
  photoConsent: boolean;
  /** The family pet: shown and sent only when petOn. */
  petOn: boolean;
  petName: string;
  petKind: string;
  petLook: string;
}

type StepKey = 'hero' | 'photos' | 'story' | 'final';
type SimpleField = Exclude<keyof BuilderForm, 'companions' | 'photos' | 'avatars'> | 'photos';
const COMPANION_FIELDS = ['name', 'relation', 'age', 'gender', 'skinTone', 'hairColor', 'hairStyle', 'eyeColor', 'glasses', 'details'] as const;
type CompanionField = (typeof COMPANION_FIELDS)[number];
/** A form field ('childName', 'photos', …) or one companion's (`companion.<key>.<field>`). */
type FieldPath = SimpleField | `companion.${string}.${CompanionField}`;
type FieldErrors = Partial<Record<string, string>>;

const HERO_FIELDS: FieldPath[] = ['childName', 'age', 'gender', 'skinTone', 'hairColor', 'hairStyle', 'eyeColor', 'glasses', 'details', 'petName', 'petKind', 'petLook'];
const STEP_FIELDS: Record<Exclude<StepKey, 'hero'>, FieldPath[]> = {
  photos: ['photos'],
  story: ['themeId', 'lessonId', 'interests', 'sidekick', 'artStyleId', 'language'],
  final: ['dedication', 'email', 'consent', 'photoConsent'],
};

const FIELD_IDS: Record<SimpleField, string> = {
  childName: 'sb-child-name',
  age: 'sb-age',
  gender: 'sb-gender',
  skinTone: 'sb-skin-tone',
  hairColor: 'sb-hair-color',
  hairStyle: 'sb-hair-style',
  eyeColor: 'sb-eye-color',
  glasses: 'sb-glasses',
  details: 'sb-details',
  photos: 'sb-photos-input',
  themeId: 'sb-theme',
  lessonId: 'sb-lesson',
  interests: 'sb-interests',
  sidekick: 'sb-sidekick',
  artStyleId: 'sb-art-style',
  language: 'sb-language',
  dedication: 'sb-dedication',
  email: 'sb-email',
  consent: 'sb-consent',
  photoConsent: 'sb-photo-consent',
  petOn: 'sb-pet-on',
  petName: 'sb-pet-name',
  petKind: 'sb-pet-kind',
  petLook: 'sb-pet-look',
};

const APPEARANCE_FIELDS: AppearanceField[] = ['skinTone', 'hairColor', 'hairStyle', 'eyeColor'];

const companionField = (key: string, field: CompanionField): FieldPath => `companion.${key}.${field}`;

function fieldId(field: FieldPath): string {
  const companion = /^companion\.([^.]+)\.(\w+)$/.exec(field);
  if (companion) return `sb-companion-${companion[1]}-${companion[2]}`;
  return FIELD_IDS[field as SimpleField] || field;
}

let companionCounter = 0;
function newCompanion(): CompanionForm {
  companionCounter += 1;
  return {
    key: `cmp${companionCounter}`,
    name: '',
    relation: '',
    age: '',
    gender: '',
    skinTone: '',
    hairColor: '',
    hairStyle: '',
    eyeColor: '',
    glasses: false,
    details: '',
  };
}

/** 'big sister' -> 'big_sister' (i18n key for a relation from GET /config). */
const relationSlug = (value: string) => appearanceSlug(value);

const SWATCHES: Partial<Record<AppearanceField, Record<string, string>>> = {
  skinTone: SKIN_TONE_SWATCHES,
  hairColor: HAIR_COLOR_SWATCHES,
  eyeColor: EYE_COLOR_SWATCHES,
};

const ART_STYLE_EMOJI: Record<string, string> = {
  watercolor: '🎨',
  animated3d: '🎬',
  classic: '📚',
  cartoon: '✏️',
};

/** The photo and face errors of POST /books: shown on the photo step (on the photo itself when the field says which). */
const PHOTO_ERRORS = new Set([
  'photo_too_large',
  'photo_unsupported',
  'photo_too_small',
  'photo_not_supported',
  'photos_too_large',
  'photo_face_too_small',
  'too_many_refs',
  'invalid_face_box',
]);
/** Server failures with their own words (anything else is the generic message). */
const SERVER_ERRORS = new Set(['create_failed', 'database_unavailable', 'server_error', 'payments_unavailable']);

const DEFAULT_CHILD_RELATIONS = ['big sister', 'big brother', 'friend'];

/** The avatar errors of POST /books: shown on the photo step, where avatars are picked. */
const AVATAR_ERRORS = new Set([
  'sign_in_required',
  'avatar_not_found',
  'avatar_not_ready',
  'avatar_failed',
  'avatar_unavailable',
  'avatar_and_photos',
  'avatar_not_supported',
  'avatars_unavailable',
]);

// The form kept across a sign-in from the photo step (to use a FaceLock
// avatar): this tab only, an hour at most, never the photos (they stay on
// the device only while the page is open).
const DRAFT_KEY = 'storybook_builder_draft_v1';
const DRAFT_TTL_MS = 60 * 60 * 1000;

const authTokenNow = (): string => {
  try {
    return localStorage.getItem('auth_token') || '';
  } catch {
    return '';
  }
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
// Same rule as the backend (routes/storybook.js NAME_RE): starts with a
// letter; letters, accents, spaces, apostrophes, dots and hyphens after that.
const NAME_RE = /^[\p{L}\p{M}][\p{L}\p{M}' .-]*$/u;
// A letter the printed book's fonts cannot draw: they cover Latin (including
// Vietnamese) and Cyrillic. The backend refuses the rest too
// (services/storybook/pdf.js unprintableLetters): it would print blank.
const OFF_SCRIPT_RE = /[^\P{L}\p{Script=Latin}\p{Script=Cyrillic}]/u;

/** Phones type curly apostrophes (D’Andre); the backend accepts the straight one. */
function normalizeName(value: string): string {
  return value.replace(/[‘’ʼ]/g, "'").replace(/\s+/g, ' ').trim();
}

function stepOf(field: FieldPath): StepKey {
  if (field.startsWith('companion.') || HERO_FIELDS.includes(field)) return 'hero';
  if (STEP_FIELDS.photos.includes(field)) return 'photos';
  if (STEP_FIELDS.story.includes(field)) return 'story';
  if (STEP_FIELDS.final.includes(field)) return 'final';
  return 'hero';
}

/**
 * Server `field` -> form field: 'childName', 'appearance.skinTone',
 * 'companions[1].name', 'companions.0.appearance.hairColor', and the legacy
 * 'companion.name' (the first companion). `sentCompanions` are the keys of the
 * companions in the order they were sent.
 */
function fieldFromServer(field: string | undefined, sentCompanions: string[]): FieldPath | null {
  if (!field) return null;
  const companion = /^companions?(?:\[(\d+)\]|\.(\d+))?(?:\.(?:appearance\.)?(\w+))?$/.exec(field);
  if (companion) {
    const key = sentCompanions[Number(companion[1] ?? companion[2] ?? 0)];
    if (!key) return null;
    const sub = companion[3] as CompanionField | undefined;
    return companionField(key, sub && (COMPANION_FIELDS as readonly string[]).includes(sub) ? sub : 'name');
  }
  const pet = /^pet\.(name|kind|look)$/.exec(field);
  if (pet) return `pet${pet[1].charAt(0).toUpperCase()}${pet[1].slice(1)}` as FieldPath;
  const key = field.replace(/^appearance\./, '');
  return key in FIELD_IDS ? (key as FieldPath) : null;
}

/** 'photos[2]' / 'faces[0]' -> the photo it points at, from what was sent. */
function photoFromServer(field: string | undefined, sent: StorybookPhotoPayload['sent'] | null): string | null {
  const match = field ? /^(photos|faces)\[(\d+)\]/.exec(field) : null;
  if (!match || !sent) return null;
  const index = Number(match[2]);
  return match[1] === 'photos' ? sent.photos[index] ?? null : sent.faces[index]?.photo ?? null;
}

function focusField(field: FieldPath) {
  const el = document.getElementById(fieldId(field));
  if (!el) return;
  const focusable = el.matches('input, select, textarea, button')
    ? el
    : el.querySelector<HTMLElement>('[role="radio"][data-state="checked"], [role="radio"], input, select, textarea, button');
  (focusable || el).focus?.();
}

function describedBy(id: string, parts: { help?: boolean; error?: boolean }): string | undefined {
  const ids = [parts.help ? `${id}-help` : null, parts.error ? `${id}-error` : null].filter(Boolean);
  return ids.length ? ids.join(' ') : undefined;
}

/** A tagged face's colours are read once it has stopped moving (a hand-placed box is dragged). */
const COLOUR_READ_DELAY_MS = 300;

/** The colour chips a look still needs to be drawn from a photo (§12): a bald head needs no hair colour. */
function colourGaps(look: Look): StorybookColourField[] {
  return STORYBOOK_COLOUR_FIELDS.filter((field) => !look[field] && !(field === 'hairColor' && isBaldHairStyle(look.hairStyle)));
}

/** The appearance sent for a look: a bald head has no hair colour. */
function appearanceOf(look: Look): StorybookAppearance {
  return {
    skinTone: look.skinTone,
    hairColor: isBaldHairStyle(look.hairStyle) ? '' : look.hairColor,
    hairStyle: look.hairStyle,
    eyeColor: look.eyeColor,
    glasses: look.glasses,
    details: look.details.trim(),
  };
}

// ---------------------------------------------------------------------------
// Field chrome: label, optional tag, help text, counter and error.
// ---------------------------------------------------------------------------

interface FieldProps {
  id: string;
  label: ReactNode;
  /** The control is a radio group: render the label as text it can point at. */
  group?: boolean;
  optional?: boolean;
  help?: string;
  error?: string;
  counter?: { count: number; max: number };
  className?: string;
  children: ReactNode;
}

function Field({ id, label, group, optional, help, error, counter, className, children }: FieldProps) {
  const { t } = useTranslation();
  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-baseline justify-between gap-3">
        {group ? (
          <span id={`${id}-label`} className="text-sm font-semibold leading-none">
            {label}
          </span>
        ) : (
          <Label htmlFor={id} className="font-semibold">
            {label}
          </Label>
        )}
        {optional && <span className="text-xs text-muted-foreground">{t('storybookPage.builder.optional')}</span>}
      </div>
      {children}
      {(help || counter) && (
        <div className="flex items-start justify-between gap-3 text-xs text-muted-foreground">
          {help ? <p id={`${id}-help`} className="leading-relaxed">{help}</p> : <span />}
          {counter && (
            <span className="shrink-0 tabular-nums" aria-hidden="true">
              {t('storybookPage.builder.charCount', { count: counter.count, max: counter.max })}
            </span>
          )}
        </div>
      )}
      {error && (
        <p id={`${id}-error`} className="text-sm font-medium text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

const selectClass =
  'flex h-11 w-full rounded-md border bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm';

// ---------------------------------------------------------------------------
// The wizard
// ---------------------------------------------------------------------------

interface StorybookBuilderProps {
  config: StorybookConfig;
  /** Redeeming a gift (/storybook/gift/redeem): the book is created already paid by it. */
  gift?: { code: string; fromName: string; childName?: string; email?: string };
}

const GIFT_ERRORS = new Set(['gift_not_found', 'gift_not_paid', 'gift_already_used', 'gift_refunded']);

export function StorybookBuilder({ config, gift }: StorybookBuilderProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const limits = config.limits;
  const uiLang = storybookUiLanguage(i18n.language);

  const [form, setForm] = useState<BuilderForm>(() => {
    const languages = config.languages?.length ? config.languages : (['en', 'es'] as StorybookLanguage[]);
    const lessons = config.lessons || [];
    return {
      childName: gift?.childName || '',
      age: '',
      gender: '',
      skinTone: '',
      hairColor: '',
      hairStyle: '',
      eyeColor: '',
      glasses: false,
      details: '',
      companions: [],
      photos: [],
      avatars: {},
      themeId: '',
      lessonId: lessons.some((lesson) => lesson.id === 'none') ? 'none' : '',
      interests: '',
      sidekick: '',
      artStyleId: config.artStyles?.[0]?.id || '',
      language: languages.includes(uiLang) ? uiLang : languages[0],
      dedication: '',
      email: gift?.email || '',
      consent: false,
      photoConsent: false,
      petOn: false,
      petName: '',
      petKind: '',
      petLook: '',
    };
  });
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [photoErrors, setPhotoErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  /** Per person key: what their best tagged face read as (§12), once read; absent while none is tagged. */
  const [photoReads, setPhotoReads] = useState<Record<string, StorybookPhotoRead>>({});
  const headingRef = useRef<HTMLHeadingElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const stepChanged = useRef(false);
  /** What the last POST carried, so a server error on companions[i], photos[i] or faces[i] lands on the right thing. */
  const lastSent = useRef<{ companions: string[]; photos: StorybookPhotoPayload['sent'] | null }>({ companions: [], photos: null });
  const signedIn = Boolean(authTokenNow());

  // Back from signing in to use an avatar: the form as it was, on the photo step.
  useEffect(() => {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(DRAFT_KEY);
      if (raw) sessionStorage.removeItem(DRAFT_KEY);
    } catch {
      raw = null;
    }
    if (!raw) return;
    try {
      const draft = JSON.parse(raw) as { at: number; step: StepKey; form: Partial<BuilderForm> };
      if (!draft || !draft.form || Date.now() - Number(draft.at) > DRAFT_TTL_MS) return;
      const companions = Array.isArray(draft.form.companions) ? draft.form.companions : [];
      // New companions must never reuse a restored key.
      for (const c of companions) {
        const n = Number(String(c.key).replace(/^cmp/, ''));
        if (Number.isFinite(n) && n > companionCounter) companionCounter = n;
      }
      setForm((prev) => ({ ...prev, ...draft.form, companions, photos: [], avatars: draft.form.avatars || {} }));
      const index = steps.indexOf(draft.step);
      if (index >= 0) {
        stepChanged.current = true;
        setStep(index);
      }
      window.setTimeout(() => topRef.current?.scrollIntoView({ block: 'start' }), 50);
    } catch {
      /* a broken draft is simply not restored */
    }
    // Once, on the first render: steps only depends on the config.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Keep the form (not its photos) for the trip to sign in or to pay, back on the photo step. */
  const saveDraft = () => {
    try {
      const { photos: _photos, ...rest } = form;
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ at: Date.now(), step: 'photos', form: rest }));
    } catch {
      /* no storage: the parent fills the form again afterwards */
    }
  };

  const signInForAvatar = (mode: 'login' | 'signup') => {
    saveDraft();
    trackEvent('storybook_avatar_sign_in', { mode });
    navigate(`/${mode}?redirect_to=${encodeURIComponent('/storybook#builder')}`);
  };

  // The photo step exists only when the backend takes photos (an older one sends no maxPhotos).
  const photosEnabled = (limits.maxPhotos ?? 0) > 0;
  const steps = useMemo<StepKey[]>(
    () => (photosEnabled ? ['hero', 'photos', 'story', 'final'] : ['hero', 'story', 'final']),
    [photosEnabled],
  );
  const stepKey = steps[Math.min(step, steps.length - 1)];
  const lastStep = steps.length - 1;
  const tagLimits = useMemo(() => storybookTagLimits(limits), [limits]);

  // Move focus to the new step's heading (not on first render: that would
  // yank the page down to the builder on load).
  useEffect(() => {
    if (!stepChanged.current) return;
    headingRef.current?.focus({ preventScroll: true });
    topRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [step]);

  const clearError = (field: FieldPath) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const update = <K extends Exclude<keyof BuilderForm, 'companions' | 'photos' | 'avatars'>>(key: K, value: BuilderForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    clearError(key);
  };

  const setPhotos = (next: SetStateAction<StorybookPhotoItem[]>) => {
    setForm((prev) => ({ ...prev, photos: typeof next === 'function' ? next(prev.photos) : next }));
    clearError('photos');
  };

  const goToStep = (next: StepKey) => {
    const index = steps.indexOf(next);
    if (index < 0) return;
    stepChanged.current = true;
    setStep(index);
  };

  const ageOptions = useMemo(() => {
    const min = Math.max(0, limits.minAge ?? 1);
    const max = Math.max(min, limits.maxAge ?? 12);
    return Array.from({ length: max - min + 1 }, (_, i) => min + i);
  }, [limits.minAge, limits.maxAge]);

  const companionAgeOptions = useMemo(() => {
    const range = config.cast?.companionAge;
    const min = Math.max(1, range?.min ?? 1);
    const max = Math.max(min, range?.max ?? 17);
    return Array.from({ length: max - min + 1 }, (_, i) => min + i);
  }, [config.cast?.companionAge]);

  const appearanceChoices = (field: AppearanceField): StorybookChoice[] =>
    (config.appearance?.[field] || []).map((value) => {
      const slug = appearanceSlug(value);
      return {
        value,
        label: t(`storybookPage.appearance.${field}.${slug}`, { defaultValue: value }),
        swatch: SWATCHES[field]?.[slug],
      };
    });

  // The pet section appears only once the backend offers pet kinds.
  const petKinds = Array.isArray(config.petKinds) ? config.petKinds : [];
  const petAvailable = petKinds.length > 0;
  // The companion section appears only once the backend offers relations.
  const relations = Array.isArray(config.relations) ? config.relations : [];
  const companionAvailable = relations.length > 0;
  // Two at most: the cast ids are hero, c1 and c2. An older backend takes one.
  const maxCompanions = Math.min(2, Math.max(0, config.cast?.maxCompanions ?? 1));
  const childRelations = config.cast?.childRelations?.length ? config.cast.childRelations : DEFAULT_CHILD_RELATIONS;
  // Every relation but a friend may be photographed; the child always may.
  const photoRelations = Array.isArray(config.photoRelations) ? config.photoRelations : [];
  const relationLabel = (value: string) =>
    value ? t(`storybookPage.relations.${relationSlug(value)}`, { defaultValue: value }) : '';
  const companionTitle = (index: number) => t('storybookPage.fields.companion.cardTitle', { n: index + 1 });

  // The parent's FaceLock avatars (signed in, photo step on): polled while one is being checked or trains.
  const crewState = useFaceLockCrew(photosEnabled && signedIn, { poll: true });
  const people = useMemo<StorybookTaggablePerson[]>(
    () => [
      {
        key: 'hero',
        id: 'hero',
        name: normalizeName(form.childName) || t('storybookPage.character.placeholderName'),
        photoAllowed: true,
      },
      ...form.companions.map((c, index) => ({
        key: c.key,
        id: index === 0 ? ('c1' as const) : ('c2' as const),
        name: normalizeName(c.name) || relationLabel(c.relation) || companionTitle(index),
        // Drawn from a FaceLock avatar instead: never tagged in a photo too.
        photoAllowed: Boolean(c.relation) && photoRelations.includes(c.relation) && !form.avatars[c.key],
        ...(form.avatars[c.key]
          ? { avatar: crewState.crew?.avatars.find((a) => a.id === form.avatars[c.key])?.name || t('storybookPage.avatars.title') }
          : {}),
      })),
    ],
    // relationLabel/companionTitle only read t, which changes with the language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.childName, form.companions, form.avatars, crewState.crew, photoRelations, t],
  );
  /** The grown-ups of the cast a FaceLock avatar may draw (never a child: the hero, a sibling, a friend). */
  const grownUps = useMemo(
    () =>
      form.companions
        .filter((c) => Boolean(c.relation) && photoRelations.includes(c.relation) && !childRelations.includes(c.relation))
        .map((c, i) => ({
          key: c.key,
          name: normalizeName(c.name) || relationLabel(c.relation) || companionTitle(i),
          relation: relationLabel(c.relation),
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.companions, photoRelations, childRelations, t],
  );
  /** The avatars actually sent: only for a grown-up still in the cast. */
  const avatarCast = useMemo(
    () => (photosEnabled ? grownUps.filter((g) => form.avatars[g.key]).map((g) => ({ key: g.key, avatarId: form.avatars[g.key] })) : []),
    [photosEnabled, grownUps, form.avatars],
  );
  const sendingPhotos = photosEnabled && (avatarCast.length > 0 || form.photos.some((photo) => photoIsUsed(photo, people, tagLimits)));
  /** Who is drawn from a photo or an avatar: their skin tone and hair colour are required (§12). */
  const photographed = useMemo(
    () =>
      new Set(
        photosEnabled
          ? [
              ...people.filter((person) => hasSentFace(form.photos, person.key, people, tagLimits)).map((person) => person.key),
              ...avatarCast.map((a) => a.key),
            ]
          : [],
      ),
    [photosEnabled, form.photos, people, tagLimits, avatarCast],
  );

  /** A grown-up drawn from an avatar (or back to photos): their photo tags are dropped. */
  const setAvatar = (personKey: string, avatarId: string | null) => {
    setForm((prev) => {
      const avatars = { ...prev.avatars };
      if (avatarId) avatars[personKey] = avatarId;
      else delete avatars[personKey];
      return { ...prev, avatars, photos: avatarId ? untagPerson(prev.photos, personKey) : prev.photos };
    });
    setFormError(null);
  };

  const genderChoices: StorybookChoice[] = (config.genders?.length ? config.genders : ['boy', 'girl', 'child']).map(
    (gender) => ({ value: gender, label: t(`storybookPage.fields.gender.${gender}`, { defaultValue: gender }) }),
  );

  const themeChoices: StorybookChoice[] = (config.themes || []).map((theme) => ({
    value: theme.id,
    label: storybookOptionLabel(theme, i18n.language),
    emoji: theme.emoji || '📖',
  }));

  const artStyleChoices: StorybookChoice[] = (config.artStyles || []).map((style) => ({
    value: style.id,
    label: storybookOptionLabel(style, i18n.language),
    emoji: style.emoji || ART_STYLE_EMOJI[style.id] || '🖌️',
  }));

  const languageChoices: StorybookChoice[] = (config.languages?.length ? config.languages : ['en', 'es']).map(
    (lang) => ({ value: lang, label: t(`storybookPage.fields.language.${lang}`, { defaultValue: lang }) }),
  );

  // ----------------------------------------------------------- companions ---

  const addCompanion = () => {
    if (form.companions.length >= maxCompanions) return;
    const companion = newCompanion();
    setForm((prev) => ({ ...prev, companions: [...prev.companions, companion] }));
    window.setTimeout(() => focusField(companionField(companion.key, 'name')), 0);
  };

  const removeCompanion = (key: string) => {
    setForm((prev) => ({
      ...prev,
      companions: prev.companions.filter((c) => c.key !== key),
      // Their faces go back to "not chosen": nobody is drawn from them any more.
      photos: untagPerson(prev.photos, key),
      avatars: Object.fromEntries(Object.entries(prev.avatars).filter(([k]) => k !== key)),
    }));
    setErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([field]) => !field.startsWith(`companion.${key}.`))));
  };

  const updateCompanion = <K extends keyof Omit<CompanionForm, 'key'>>(key: string, field: K, value: CompanionForm[K]) => {
    setForm((prev) => {
      const companions = prev.companions.map((c) => {
        if (c.key !== key) return c;
        const next = { ...c, [field]: value };
        if (field === 'relation') {
          // Age is for a sibling or friend, gender for a friend: a new relation drops what no longer applies.
          if (!childRelations.includes(String(value))) next.age = '';
          if (value !== 'friend') next.gender = '';
        }
        return next;
      });
      // A friend is never photographed: their faces go back to "not chosen".
      const photos = field === 'relation' && !photoRelations.includes(String(value)) ? untagPerson(prev.photos, key) : prev.photos;
      // An avatar draws a grown-up only: a new relation that isn't one drops it.
      const grownUp = photoRelations.includes(String(value)) && !childRelations.includes(String(value));
      const avatars = field === 'relation' && !grownUp ? Object.fromEntries(Object.entries(prev.avatars).filter(([k]) => k !== key)) : prev.avatars;
      return { ...prev, companions, photos, avatars };
    });
    clearError(companionField(key, field as CompanionField));
  };

  // ------------------------------------------------- colours from photos ---
  // The worker sees faces in grayscale, so skin and hair colour in the
  // drawings come only from the chips (docs/storybook-identity-lock.md §12).
  // Everyone drawn from a photo must have both chosen (a bald head: skin
  // only): the photo step's Next and the submit check it, and the photo
  // step shows those chips for each photographed companion (the child's are
  // required on step 1 already). The photo NEVER picks a chip: whenever a
  // person's best tagged face changes it is read in the browser, and when the
  // estimator is sure the panel only suggests its chips with a "Use these"
  // button the parent may click.

  /** Per person: the face last read (photo, face, box, who), '' for none. */
  const colourReads = useRef<Record<string, string>>({});
  const colourTimers = useRef<Record<string, number>>({});
  const colourChips = useMemo(
    () => ({ skinTone: config.appearance?.skinTone || [], hairColor: config.appearance?.hairColor || [] }),
    [config.appearance?.skinTone, config.appearance?.hairColor],
  );

  const lookOf = (from: BuilderForm, personKey: string): Look | undefined =>
    personKey === 'hero' ? from : from.companions.find((c) => c.key === personKey);

  /** The parent takes the photo's suggestion: those chips are set, as if clicked. */
  const takeSuggestion = (personKey: string, suggestion: StorybookPhotoSuggestion) => {
    for (const field of STORYBOOK_COLOUR_FIELDS) {
      const value = suggestion[field];
      if (!value) continue;
      if (personKey === 'hero') update(field, value);
      else updateCompanion(personKey, field, value);
    }
  };

  /** Who a person is to the estimator: the child and the child relations (or anyone under 13) are children; a bald look reads no hair. */
  const colourPersonOf = (personKey: string) => {
    if (personKey === 'hero') return storybookColourPerson(form, true);
    const c = form.companions.find((x) => x.key === personKey);
    const age = Number(c?.age);
    return storybookColourPerson(c || {}, Boolean(c && (childRelations.includes(c.relation) || (age > 0 && age < 13))));
  };

  useEffect(() => {
    if (!photosEnabled) return;
    const keys = new Set(people.map((person) => person.key));
    for (const key of Object.keys(colourReads.current)) {
      if (keys.has(key)) continue;
      window.clearTimeout(colourTimers.current[key]);
      delete colourReads.current[key];
      delete colourTimers.current[key];
    }
    setPhotoReads((prev) =>
      Object.keys(prev).every((key) => keys.has(key)) ? prev : Object.fromEntries(Object.entries(prev).filter(([key]) => keys.has(key))),
    );
    for (const person of people) {
      const best = bestTaggedFace(form.photos, person.key, people, tagLimits);
      const who = colourPersonOf(person.key);
      const box = best ? payloadFaceBox(best.face.box) : null;
      const read = best ? `${best.photo.key}|${best.face.key}|${box.join(',')}|${who.child ? 'child' : ''}|${who.bald ? 'bald' : ''}` : '';
      if ((colourReads.current[person.key] ?? '') === read) continue;
      colourReads.current[person.key] = read;
      window.clearTimeout(colourTimers.current[person.key]);
      if (!best) {
        setPhotoReads((prev) => {
          if (!(person.key in prev)) return prev;
          const next = { ...prev };
          delete next[person.key];
          return next;
        });
        continue;
      }
      colourTimers.current[person.key] = window.setTimeout(() => {
        void estimateStorybookFaceColours(best.photo.dataUrl, box, who).then((estimate) => {
          // A newer tag (or none) for this person since: this answer is stale.
          if (colourReads.current[person.key] !== read) return;
          const answer: StorybookPhotoRead = estimate || { skinTone: null, hairColor: null, confidence: { skinTone: 0, hairColor: 0 } };
          setPhotoReads((prev) => ({ ...prev, [person.key]: answer }));
        });
      }, COLOUR_READ_DELAY_MS);
    }
    // colourPersonOf only reads the form (its hair styles and ages are deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.photos, people, tagLimits, photosEnabled, form.hairStyle, form.companions]);

  useEffect(() => () => Object.values(colourTimers.current).forEach((timer) => window.clearTimeout(timer)), []);

  /** "Medium skin · Black hair": a suggestion in the chips' own words. */
  const suggestionText = (suggestion: StorybookPhotoSuggestion) =>
    STORYBOOK_COLOUR_FIELDS.filter((field) => suggestion[field])
      .map((field) => {
        const value = t(`storybookPage.appearance.${field}.${appearanceSlug(suggestion[field] as string)}`, { defaultValue: suggestion[field] });
        return t(`storybookPage.photos.colours.${field === 'skinTone' ? 'skin' : 'hair'}`, { value, lower: value.toLocaleLowerCase(uiLang) });
      })
      .join(' · ');

  /** One suggestion line: what the photo looks like, and a button to take it (the only way it changes a chip). */
  const suggestionLine = (personKey: string, suggestion: StorybookPhotoSuggestion, textKey: 'suggest' | 'suggestHero') => {
    const count = STORYBOOK_COLOUR_FIELDS.filter((field) => suggestion[field]).length;
    if (!count) return null;
    const id = `sb-photo-colours-${personKey}-suggestion`;
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-dashed border-primary/50 bg-card px-3 py-2 text-sm">
        <span id={id}>{t(`storybookPage.photos.colours.${textKey}`, { value: suggestionText(suggestion) })}</span>
        <Button type="button" variant="outline" size="sm" className="h-8 rounded-full" aria-describedby={id} onClick={() => takeSuggestion(personKey, suggestion)}>
          {t('storybookPage.photos.colours.use', { count })}
        </Button>
      </div>
    );
  };

  /**
   * The photo step's colour panel. Each photographed companion: their skin
   * tone and hair colour chips (required here), and the photo's suggestion
   * when the estimator is sure. The child: only a suggestion that differs
   * from the chips chosen on step 1. Nothing when the photo can't tell.
   */
  const colourPanel = () => {
    const rows = people.flatMap((person) => {
      if (!photographed.has(person.key)) return [];
      const look = lookOf(form, person.key);
      if (!look) return [];
      const suggestion = photoSuggestion(look, photoReads[person.key], colourChips);
      if (person.key === 'hero') {
        const line = suggestionLine('hero', suggestion, 'suggestHero');
        if (!line) return [];
        return [
          <div key="hero" role="group" aria-labelledby="sb-photo-colours-hero-name" className="space-y-2">
            <p id="sb-photo-colours-hero-name" className="font-semibold">
              {person.name}
            </p>
            {line}
          </div>,
        ];
      }
      const nameId = `sb-photo-colours-${person.key}-name`;
      return [
        <div key={person.key} role="group" aria-labelledby={nameId} className="space-y-4">
          <p id={nameId} className="font-semibold">
            {person.name}
          </p>
          {STORYBOOK_COLOUR_FIELDS.map((field) =>
            chipField(look, companionField(person.key, field), field, (value) => updateCompanion(person.key, field, value), false),
          )}
          {suggestionLine(person.key, suggestion, 'suggest')}
        </div>,
      ];
    });
    if (!rows.length) return null;
    return (
      <section aria-labelledby="sb-photo-colours-title" className="space-y-4 rounded-2xl border border-border bg-muted/40 p-4 sm:p-5">
        <div>
          <h4 id="sb-photo-colours-title" className="text-base font-semibold">
            {t('storybookPage.photos.colours.title')}
          </h4>
          <p className="mt-1 text-sm text-muted-foreground">{t('storybookPage.photos.colours.help')}</p>
        </div>
        {rows}
      </section>
    );
  };

  // ----------------------------------------------------------- validation ---

  const tooLong = (value: string, max: number) => max > 0 && value.trim().length > max;

  const nameError = (value: string, required: string): string | undefined => {
    const name = normalizeName(value);
    if (!name) return required;
    if (tooLong(name, limits.nameMax)) return t('storybookPage.validation.tooLong', { max: limits.nameMax });
    if (!NAME_RE.test(name)) return t('storybookPage.validation.nameInvalid');
    if (OFF_SCRIPT_RE.test(name)) return t('storybookPage.validation.nameScript');
    return undefined;
  };

  const validateStep = (target: StepKey): FieldErrors => {
    const e: FieldErrors = {};
    if (target === 'hero') {
      e.childName = nameError(form.childName, t('storybookPage.validation.nameRequired'));
      if (!form.age) e.age = t('storybookPage.validation.ageRequired');
      if (!form.gender) e.gender = t('storybookPage.validation.genderRequired');
      for (const field of APPEARANCE_FIELDS) {
        // A bald head needs no hair colour (the prompt ignores it).
        if (field === 'hairColor' && isBaldHairStyle(form.hairStyle)) continue;
        if (!form[field]) e[field] = t(`storybookPage.validation.${field}Required`);
      }
      if (tooLong(form.details, limits.detailsMax)) e.details = t('storybookPage.validation.tooLong', { max: limits.detailsMax });
      if (petAvailable && form.petOn) {
        e.petName = nameError(form.petName, t('storybookPage.pet.nameRequired'));
        if (!form.petKind) e.petKind = t('storybookPage.pet.kindRequired');
        if (tooLong(form.petLook, limits.detailsMax)) e.petLook = t('storybookPage.validation.tooLong', { max: limits.detailsMax });
      }
      if (companionAvailable) {
        for (const c of form.companions) {
          e[companionField(c.key, 'name')] = nameError(c.name, t('storybookPage.validation.companionNameRequired'));
          if (!c.relation) e[companionField(c.key, 'relation')] = t('storybookPage.validation.companionRelationRequired');
          if (tooLong(c.details, limits.detailsMax)) {
            e[companionField(c.key, 'details')] = t('storybookPage.validation.tooLong', { max: limits.detailsMax });
          }
        }
      }
    } else if (target === 'story') {
      if (!form.themeId) e.themeId = t('storybookPage.validation.themeRequired');
      if (!form.artStyleId) e.artStyleId = t('storybookPage.validation.artStyleRequired');
      if (tooLong(form.interests, limits.interestsMax)) e.interests = t('storybookPage.validation.tooLong', { max: limits.interestsMax });
      if (tooLong(form.sidekick, limits.sidekickMax)) e.sidekick = t('storybookPage.validation.tooLong', { max: limits.sidekickMax });
    } else if (target === 'final') {
      if (tooLong(form.dedication, limits.dedicationMax)) e.dedication = t('storybookPage.validation.tooLong', { max: limits.dedicationMax });
      else if (OFF_SCRIPT_RE.test(form.dedication)) e.dedication = t('storybookPage.validation.dedicationScript');
      const email = form.email.trim();
      if (!email) e.email = t('storybookPage.validation.emailRequired');
      else if (!EMAIL_RE.test(email)) e.email = t('storybookPage.validation.emailInvalid');
      if (!form.consent) e.consent = t('storybookPage.validation.consentRequired');
      if (sendingPhotos && !form.photoConsent) e.photoConsent = t('storybookPage.validation.photoConsentRequired');
    } else if (target === 'photos') {
      // Photos are optional, but whoever is drawn from one needs their colour
      // chips (§12; the child's are required on step 1 already).
      for (const c of form.companions) {
        const gaps = photographed.has(c.key) ? colourGaps(c) : [];
        gaps.forEach((field, i) => {
          e[companionField(c.key, field)] =
            i === 0
              ? t(`storybookPage.validation.photoColours.${gaps.length > 1 ? 'both' : field}`, { name: people.find((p) => p.key === c.key)?.name || '' })
              : t(`storybookPage.validation.${field}Required`);
        });
      }
    }
    return Object.fromEntries(Object.entries(e).filter(([, message]) => Boolean(message)));
  };

  const fieldsOf = (target: StepKey): FieldPath[] => {
    if (target === 'hero') return [...HERO_FIELDS, ...form.companions.flatMap((c) => COMPANION_FIELDS.map((f) => companionField(c.key, f)))];
    if (target === 'photos') return [...STEP_FIELDS.photos, ...form.companions.flatMap((c) => STORYBOOK_COLOUR_FIELDS.map((f) => companionField(c.key, f)))];
    return STEP_FIELDS[target];
  };

  /** Shows a step's errors; returns true when the step is valid. */
  const checkStep = (target: StepKey): boolean => {
    const stepErrors = validateStep(target);
    const keys = Object.keys(stepErrors) as FieldPath[];
    if (!keys.length) return true;
    setErrors((prev) => ({ ...prev, ...stepErrors }));
    setFormError(t('storybookPage.validation.fixBelow'));
    if (target !== stepKey) goToStep(target);
    const first = fieldsOf(target).find((field) => stepErrors[field]) || keys[0];
    window.setTimeout(() => focusField(first), 0);
    return false;
  };

  const goNext = () => {
    if (photoBusy) return;
    if (!checkStep(stepKey)) return;
    trackEvent('storybook_form_step_completed', { step: step + 1, step_name: stepKey });
    setFormError(null);
    stepChanged.current = true;
    setStep(Math.min(lastStep, step + 1));
  };

  const goBack = () => {
    setFormError(null);
    if (step > 0) {
      stepChanged.current = true;
      setStep(step - 1);
    }
  };

  // -------------------------------------------------------- server errors ---

  const showApiError = (err: unknown) => {
    const apiError = err instanceof StorybookApiError ? err : null;
    const code = apiError?.code || 'generic';
    if (PHOTO_ERRORS.has(code) && photosEnabled) {
      const message = t(`storybookPage.errors.${code}`);
      const photoKey = photoFromServer(apiError?.field, lastSent.current.photos);
      if (photoKey) setPhotoErrors((prev) => ({ ...prev, [photoKey]: message }));
      setErrors((prev) => ({ ...prev, photos: message }));
      goToStep('photos');
      setFormError(message);
      return;
    }
    if (AVATAR_ERRORS.has(code) && photosEnabled) {
      goToStep('photos');
      setFormError(t(`storybookPage.avatars.errors.${code}`, { defaultValue: t('storybookPage.avatars.errors.avatar_unavailable') }));
      if (code === 'avatar_not_found' || code === 'avatar_failed') void crewState.refresh();
      return;
    }
    if (GIFT_ERRORS.has(code)) {
      setFormError(t(`storybookGift.redeem.errors.${code}`));
      return;
    }
    switch (code) {
      case 'photo_consent_required':
        setErrors((prev) => ({ ...prev, photoConsent: t('storybookPage.validation.photoConsentRequired') }));
        goToStep('final');
        setFormError(t('storybookPage.errors.photo_consent_required'));
        return;
      case 'invalid_input': {
        const field = fieldFromServer(apiError?.field, lastSent.current.companions);
        const colour = field ? /^companion\.([^.]+)\.(skinTone|hairColor)$/.exec(field) : null;
        if (field && colour && photographed.has(colour[1])) {
          // A photographed companion without their colour chips (§12): they are picked on the photo step.
          const name = people.find((p) => p.key === colour[1])?.name || '';
          setErrors((prev) => ({ ...prev, [field]: t(`storybookPage.validation.photoColours.${colour[2]}`, { name }) }));
          goToStep('photos');
          window.setTimeout(() => focusField(field), 0);
          setFormError(t('storybookPage.validation.fixBelow'));
          return;
        }
        if (field) {
          setErrors((prev) => ({ ...prev, [field]: t('storybookPage.errors.invalidField') }));
          goToStep(stepOf(field));
          window.setTimeout(() => focusField(field), 0);
        }
        setFormError(t('storybookPage.errors.invalid_input'));
        return;
      }
      case 'consent_required':
        setErrors((prev) => ({ ...prev, consent: t('storybookPage.validation.consentRequired') }));
        goToStep('final');
        setFormError(t('storybookPage.errors.consent_required'));
        return;
      case 'rate_limited':
      case 'daily_limit':
        setFormError(t('storybookPage.errors.busy'));
        return;
      case 'not_enabled':
        setFormError(t('storybookPage.errors.not_enabled'));
        return;
      case 'network_error':
        setFormError(t('storybookPage.errors.network'));
        return;
      default:
        setFormError(t(SERVER_ERRORS.has(code) ? `storybookPage.errors.${code}` : 'storybookPage.errors.generic'));
    }
  };

  // --------------------------------------------------------------- submit ---

  const submit = async () => {
    if (submitting || photoBusy) return;
    for (const target of steps) {
      if (!checkStep(target)) return;
    }
    trackEvent('storybook_form_step_completed', { step: steps.length, step_name: 'final' });
    setFormError(null);
    setSubmitting(true);

    const age = Number(form.age);
    const cast = companionAvailable ? form.companions : [];
    const companions: StorybookCompanion[] = cast.map((c) => ({
      name: normalizeName(c.name),
      relation: c.relation,
      ...(childRelations.includes(c.relation) && c.age ? { age: Number(c.age) } : {}),
      ...(c.relation === 'friend' && c.gender ? { gender: c.gender } : {}),
      appearance: appearanceOf(c),
    }));
    const photoPayload = photosEnabled
      ? buildPhotoPayload(form.photos, people, tagLimits)
      : { photos: [], faces: [], sent: { photos: [], faces: [] } };
    const payload: CreateStorybookRequest = {
      childName: normalizeName(form.childName),
      age,
      gender: form.gender as StorybookGender,
      language: form.language,
      themeId: form.themeId,
      lessonId: form.lessonId || 'none',
      artStyleId: form.artStyleId,
      interests: form.interests.trim(),
      sidekick: form.sidekick.trim(),
      dedication: form.dedication.trim(),
      appearance: appearanceOf(form),
      companions,
      ...(petAvailable && form.petOn
        ? { pet: { name: normalizeName(form.petName), kind: form.petKind, look: form.petLook.trim() } }
        : {}),
      email: form.email.trim(),
      consent: true,
      // The site mark on the digital PDF (lib/site-mark.ts): on unless unticked.
      watermark: keepSiteMark(),
      ...(photoPayload.photos.length
        ? { photos: photoPayload.photos, faces: photoPayload.faces, photoConsent: true as const }
        : {}),
      ...(config.consentVersion ? { consentVersion: config.consentVersion } : {}),
      ...(gift ? { giftCode: gift.code } : {}),
    };
    const avatars = avatarCast
      .map((a) => ({ person: cast.findIndex((c) => c.key === a.key), avatarId: a.avatarId }))
      .filter((a) => a.person === 0 || a.person === 1)
      .map((a) => ({ person: (a.person === 0 ? 'c1' : 'c2') as 'c1' | 'c2', avatarId: a.avatarId }));
    if (avatars.length) Object.assign(payload, { avatars, photoConsent: true as const });
    lastSent.current = { companions: cast.map((c) => c.key), photos: photoPayload.sent };

    try {
      const created = await createStorybook(payload);
      saveStorybookToken(created.id, created.accessToken);
      rememberStorybook(created.id, new Date().toISOString());
      // Bands, ids, booleans and counts only: never a name, relation, age,
      // looks, interests, text or anything from a photo.
      trackEvent('storybook_preview_requested', {
        age_band: storybookAgeBand(age),
        language: form.language,
        theme_id: form.themeId,
        art_style_id: form.artStyleId,
        has_companion: companions.length > 0,
        companion_count: companions.length,
        photo_count: photoPayload.photos.length,
        people_with_photos: new Set(photoPayload.faces.map((face) => face.person)).size,
        avatar_count: avatars.length,
        ...(gift ? { gifted: true } : {}),
      });
      navigate(`/storybook/${encodeURIComponent(created.id)}`);
    } catch (err) {
      showApiError(err);
      setSubmitting(false);
    }
  };

  const online = useStudioOnline('storybook');
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (step < lastStep) goNext();
    else if (online) void submit();
  };

  const err = (field: FieldPath) => errors[field];
  const invalid = (field: FieldPath) => (errors[field] ? true : undefined);
  const selectedTheme = config.themes?.find((theme) => theme.id === form.themeId);
  const companionNames = form.companions.map((c) => normalizeName(c.name)).filter(Boolean);

  /** One appearance chip group (a bald head's hair colour: shown, not choosable). */
  const chipField = (look: Look, path: FieldPath, field: AppearanceField, onChange: (value: string) => void, optional: boolean) => {
    const id = fieldId(path);
    const noColour = field === 'hairColor' && isBaldHairStyle(look.hairStyle);
    const help = noColour ? t('storybookPage.fields.appearance.hairColorBald') : undefined;
    return (
      <Field key={field} id={id} group optional={optional} label={t(`storybookPage.fields.appearance.${field}`)} help={help} error={err(path)}>
        <StorybookChoiceGroup
          id={id}
          value={look[field]}
          onChange={onChange}
          options={appearanceChoices(field)}
          labelledBy={`${id}-label`}
          describedBy={describedBy(id, { help: Boolean(help), error: !!err(path) })}
          invalid={!!err(path)}
          disabled={noColour}
        />
      </Field>
    );
  };

  /**
   * The four appearance chip groups, the glasses switch and the details line,
   * for the child or a companion. `optional(field)`: whether a chip group is
   * marked optional (a companion's, except the colours of one drawn from a
   * photo).
   */
  const lookFields = (
    look: Look,
    ids: Record<AppearanceField | 'glasses' | 'details', FieldPath>,
    onChange: <K extends keyof Look>(field: K, value: Look[K]) => void,
    optional: (field: AppearanceField) => boolean,
    detailsPlaceholder: string,
    detailsHelp?: string,
  ) => {
    return (
      <>
        {APPEARANCE_FIELDS.map((field) => chipField(look, ids[field], field, (value) => onChange(field, value), optional(field)))}
        <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-4 py-3">
          <Label htmlFor={fieldId(ids.glasses)} className="cursor-pointer font-semibold">
            <span aria-hidden="true" className="mr-2">👓</span>
            {t('storybookPage.fields.appearance.glasses')}
          </Label>
          <Switch id={fieldId(ids.glasses)} checked={look.glasses} onCheckedChange={(checked) => onChange('glasses', checked)} />
        </div>
        <Field
          id={fieldId(ids.details)}
          label={t('storybookPage.fields.appearance.detailsLabel')}
          optional
          help={detailsHelp}
          error={err(ids.details)}
          counter={limits.detailsMax ? { count: look.details.length, max: limits.detailsMax } : undefined}
        >
          <Input
            id={fieldId(ids.details)}
            value={look.details}
            onChange={(e) => onChange('details', e.target.value)}
            placeholder={detailsPlaceholder}
            maxLength={limits.detailsMax || undefined}
            autoComplete="off"
            aria-invalid={invalid(ids.details)}
            aria-describedby={describedBy(fieldId(ids.details), { help: Boolean(detailsHelp), error: !!err(ids.details) })}
            className="h-11"
          />
        </Field>
      </>
    );
  };

  const companionCard = (c: CompanionForm, index: number) => {
    const f = (field: CompanionField) => companionField(c.key, field);
    const titleId = `sb-companion-${c.key}-title`;
    const childRelation = childRelations.includes(c.relation);
    return (
      <section key={c.key} aria-labelledby={titleId} className="space-y-6 rounded-2xl border border-primary/50 bg-card p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <h5 id={titleId} className="text-base font-semibold">
            {companionTitle(index)}
          </h5>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onClick={() => removeCompanion(c.key)}
            aria-label={t('storybookPage.fields.companion.removeAria', { n: index + 1 })}
          >
            <X aria-hidden="true" />
            {t('storybookPage.fields.companion.remove')}
          </Button>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field
            id={fieldId(f('name'))}
            label={t('storybookPage.fields.companion.nameLabel')}
            help={t('storybookPage.fields.companion.nameHelp')}
            error={err(f('name'))}
          >
            <Input
              id={fieldId(f('name'))}
              value={c.name}
              onChange={(e) => updateCompanion(c.key, 'name', e.target.value)}
              placeholder={t('storybookPage.fields.companion.namePlaceholder')}
              maxLength={limits.nameMax || undefined}
              autoComplete="off"
              autoCapitalize="words"
              spellCheck={false}
              aria-invalid={invalid(f('name'))}
              aria-describedby={describedBy(fieldId(f('name')), { help: true, error: !!err(f('name')) })}
              className={cn('h-11', err(f('name')) && 'border-destructive')}
            />
          </Field>
          <Field id={fieldId(f('relation'))} label={t('storybookPage.fields.companion.relationLabel')} error={err(f('relation'))}>
            <select
              id={fieldId(f('relation'))}
              value={c.relation}
              onChange={(e) => updateCompanion(c.key, 'relation', e.target.value)}
              aria-invalid={invalid(f('relation'))}
              aria-describedby={describedBy(fieldId(f('relation')), { error: !!err(f('relation')) })}
              className={cn(selectClass, err(f('relation')) ? 'border-destructive' : 'border-input')}
            >
              <option value="" disabled>
                {t('storybookPage.fields.companion.relationPlaceholder')}
              </option>
              {relations.map((relation) => (
                <option key={relation} value={relation}>
                  {relationLabel(relation)}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {(childRelation || c.relation === 'friend') && (
          <div className="grid gap-5 sm:grid-cols-2">
            {childRelation && (
              <Field
                id={fieldId(f('age'))}
                label={t('storybookPage.fields.companion.ageLabel')}
                optional
                help={t('storybookPage.fields.companion.ageHelp')}
              >
                <select
                  id={fieldId(f('age'))}
                  value={c.age}
                  onChange={(e) => updateCompanion(c.key, 'age', e.target.value)}
                  aria-describedby={describedBy(fieldId(f('age')), { help: true })}
                  className={cn(selectClass, 'border-input')}
                >
                  <option value="">{t('storybookPage.fields.companion.agePlaceholder')}</option>
                  {companionAgeOptions.map((age) => (
                    <option key={age} value={String(age)}>
                      {t('storybookPage.fields.age.option', { count: age })}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            {c.relation === 'friend' && (
              <Field id={fieldId(f('gender'))} group optional label={t('storybookPage.fields.companion.genderLabel')}>
                <StorybookChoiceGroup
                  id={fieldId(f('gender'))}
                  value={c.gender}
                  onChange={(value) => updateCompanion(c.key, 'gender', value as StorybookGender)}
                  options={genderChoices}
                  labelledBy={`${fieldId(f('gender'))}-label`}
                />
              </Field>
            )}
          </div>
        )}

        <p className="text-sm text-muted-foreground">{t('storybookPage.fields.companion.appearanceHelp')}</p>
        {lookFields(
          c,
          {
            skinTone: f('skinTone'),
            hairColor: f('hairColor'),
            hairStyle: f('hairStyle'),
            eyeColor: f('eyeColor'),
            glasses: f('glasses'),
            details: f('details'),
          },
          (field, value) => updateCompanion(c.key, field, value as CompanionForm[typeof field]),
          (field) => !(photographed.has(c.key) && (STORYBOOK_COLOUR_FIELDS as readonly string[]).includes(field)),
          t('storybookPage.fields.companion.detailsPlaceholder'),
        )}
      </section>
    );
  };

  return (
    <div ref={topRef} className="scroll-mt-24">
      {/* Progress */}
      <nav aria-label={t('storybookPage.builder.progressAria')} className="mb-6">
        <ol className={cn('grid gap-2', steps.length === 4 ? 'grid-cols-4' : 'grid-cols-3')}>
          {steps.map((key, index) => {
            const done = index < step;
            const current = index === step;
            const circle = (
              <span
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-bold transition-colors',
                  done && 'border-primary bg-primary text-primary-foreground',
                  current && 'border-primary bg-card text-primary shadow-[0_0_0_4px_hsl(var(--primary)/0.15)]',
                  !done && !current && 'border-border bg-card text-muted-foreground',
                )}
              >
                {done ? <Check className="h-4 w-4" aria-hidden="true" /> : index + 1}
              </span>
            );
            const label = (
              <span className={cn('text-xs font-semibold sm:text-sm', current ? 'text-foreground' : 'text-muted-foreground')}>
                {t(`storybookPage.builder.steps.${key}`)}
              </span>
            );
            return (
              <li key={key} aria-current={current ? 'step' : undefined}>
                {done ? (
                  <button
                    type="button"
                    onClick={() => goToStep(key)}
                    className="flex w-full flex-col items-center gap-1.5 rounded-xl p-1 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={t('storybookPage.builder.editStep', { step: t(`storybookPage.builder.steps.${key}`) })}
                  >
                    {circle}
                    {label}
                  </button>
                ) : (
                  <div className="flex flex-col items-center gap-1.5 p-1 text-center">
                    {circle}
                    {label}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted" aria-hidden="true">
          <div
            className="h-full rounded-full bg-[linear-gradient(90deg,hsl(var(--primary)),hsl(330_68%_52%))] transition-all duration-500"
            style={{ width: `${((step + 1) / steps.length) * 100}%` }}
          />
        </div>
        <p className="sr-only" aria-live="polite">
          {t('storybookPage.builder.progressLabel', { current: step + 1, total: steps.length })}
        </p>
      </nav>

      <form
        noValidate
        onSubmit={onSubmit}
        className="ph-no-capture glass-card space-y-8 p-5 sm:p-8"
        aria-labelledby="sb-step-heading"
      >
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-primary">
            {t('storybookPage.builder.progressLabel', { current: step + 1, total: steps.length })}
          </p>
          <h3
            id="sb-step-heading"
            ref={headingRef}
            tabIndex={-1}
            className="mt-1 text-2xl font-semibold focus:outline-none sm:text-3xl"
          >
            {t(`storybookPage.builder.stepTitles.${stepKey}`)}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">{t(`storybookPage.builder.stepIntros.${stepKey}`)}</p>
        </div>

        {stepKey === 'hero' && (
          <div className="space-y-8">
            {photosEnabled && (
              <section aria-labelledby="sb-faces-choice-title" data-testid="sb-faces-choice" className="space-y-3">
                <h4 id="sb-faces-choice-title" className="text-base font-semibold">
                  {t('storybookPage.builder.facesChoice.title')}
                </h4>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="flex gap-3 rounded-2xl border-2 border-primary bg-primary/5 p-4">
                    <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <Camera className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 space-y-1">
                      <p className="font-semibold">
                        {t('storybookPage.builder.facesChoice.likeness.title')}{' '}
                        <span className="ml-1 inline-block rounded-full bg-primary px-2 py-0.5 align-middle text-[11px] font-bold uppercase tracking-wide text-primary-foreground">
                          {t('storybookPage.builder.facesChoice.likeness.badge')}
                        </span>
                      </p>
                      <p className="text-sm text-muted-foreground">{t('storybookPage.builder.facesChoice.likeness.text')}</p>
                      <p className="flex items-start gap-1.5 text-sm text-muted-foreground">
                        <ScanFace aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        {t('storybookPage.builder.facesChoice.likeness.avatar')}
                      </p>
                      <div className="flex flex-col gap-2 pt-2">
                        <Button
                          type="button"
                          size="lg"
                          data-testid="sb-upload-now"
                          className="h-auto min-h-12 whitespace-normal rounded-full py-3"
                          onClick={() => {
                            goToStep('photos');
                            // Straight to the picker, in the same tap (a browser that refuses still lands on the upload step).
                            window.setTimeout(() => document.getElementById('sb-photos-input')?.click(), 0);
                          }}
                        >
                          <Camera aria-hidden="true" />
                          {t('storybookPage.builder.facesChoice.likeness.upload')}
                        </Button>
                        <Button
                          type="button"
                          size="lg"
                          variant="outline"
                          data-testid="sb-avatar-now"
                          className="h-auto min-h-12 whitespace-normal rounded-full py-3"
                          onClick={() => goToStep('photos')}
                        >
                          <ScanFace aria-hidden="true" />
                          {t('storybookPage.builder.facesChoice.likeness.useAvatar')}
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3 rounded-2xl border border-border bg-card p-4">
                    <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <Palette className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 space-y-1">
                      <p className="font-semibold">{t('storybookPage.builder.facesChoice.describe.title')}</p>
                      <p className="text-sm text-muted-foreground">{t('storybookPage.builder.facesChoice.describe.text')}</p>
                    </div>
                  </div>
                </div>
              </section>
            )}
            <div className="grid gap-5 sm:grid-cols-[1fr_12rem]">
              <Field
                id={FIELD_IDS.childName}
                label={t('storybookPage.fields.name.label')}
                help={t('storybookPage.fields.name.help')}
                error={err('childName')}
              >
                <Input
                  id={FIELD_IDS.childName}
                  value={form.childName}
                  onChange={(e) => update('childName', e.target.value)}
                  placeholder={t('storybookPage.fields.name.placeholder')}
                  maxLength={limits.nameMax || undefined}
                  autoComplete="off"
                  autoCapitalize="words"
                  spellCheck={false}
                  required
                  aria-invalid={invalid('childName')}
                  aria-describedby={describedBy(FIELD_IDS.childName, { help: true, error: !!err('childName') })}
                  className={cn('h-11', err('childName') && 'border-destructive')}
                />
              </Field>
              <Field id={FIELD_IDS.age} label={t('storybookPage.fields.age.label')} error={err('age')}>
                <select
                  id={FIELD_IDS.age}
                  value={form.age}
                  onChange={(e) => update('age', e.target.value)}
                  required
                  aria-invalid={invalid('age')}
                  aria-describedby={describedBy(FIELD_IDS.age, { error: !!err('age') })}
                  className={cn(selectClass, err('age') ? 'border-destructive' : 'border-input')}
                >
                  <option value="" disabled>
                    {t('storybookPage.fields.age.placeholder')}
                  </option>
                  {ageOptions.map((age) => (
                    <option key={age} value={String(age)}>
                      {t('storybookPage.fields.age.option', { count: age })}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field
              id={FIELD_IDS.gender}
              group
              label={t('storybookPage.fields.gender.label')}
              help={t('storybookPage.fields.gender.help')}
              error={err('gender')}
            >
              <StorybookChoiceGroup
                id={FIELD_IDS.gender}
                value={form.gender}
                onChange={(value) => update('gender', value as StorybookGender)}
                options={genderChoices}
                labelledBy={`${FIELD_IDS.gender}-label`}
                describedBy={describedBy(FIELD_IDS.gender, { help: true, error: !!err('gender') })}
                invalid={!!err('gender')}
              />
            </Field>

            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_14rem]">
              {/* Live "meet your hero" doodle: on top on phones, a sticky side
                  card on wide screens, next to everyone's choices. */}
              <div className="lg:order-last">
                <StorybookCharacterCard
                  className="lg:sticky lg:top-24"
                  name={form.childName}
                  skinTone={form.skinTone}
                  hairColor={form.hairColor}
                  hairStyle={form.hairStyle}
                  eyeColor={form.eyeColor}
                  glasses={form.glasses}
                  details={form.details}
                  companions={
                    companionAvailable
                      ? form.companions.map((c) => ({
                          name: c.name,
                          relation: relationLabel(c.relation),
                          skinTone: c.skinTone,
                          hairColor: c.hairColor,
                          hairStyle: c.hairStyle,
                          eyeColor: c.eyeColor,
                          glasses: c.glasses,
                        }))
                      : null
                  }
                />
              </div>

              <div className="min-w-0 space-y-6">
                <fieldset className="space-y-6 rounded-2xl border border-border bg-muted/40 p-4 sm:p-6">
                  <legend className="px-1 text-base font-semibold">{t('storybookPage.fields.appearance.title')}</legend>
                  <p className="-mt-3 text-sm text-muted-foreground">{t('storybookPage.fields.appearance.help')}</p>
                  {lookFields(
                    form,
                    {
                      skinTone: 'skinTone',
                      hairColor: 'hairColor',
                      hairStyle: 'hairStyle',
                      eyeColor: 'eyeColor',
                      glasses: 'glasses',
                      details: 'details',
                    },
                    (field, value) => update(field, value as BuilderForm[typeof field]),
                    () => false,
                    t('storybookPage.fields.appearance.detailsPlaceholder'),
                    t('storybookPage.fields.appearance.detailsHelp'),
                  )}
                </fieldset>

                {companionAvailable && (
                  <section
                    aria-labelledby="sb-companions-title"
                    className={cn(
                      'space-y-5 rounded-2xl border p-4 transition-colors sm:p-6',
                      form.companions.length ? 'border-border bg-muted/40' : 'border-dashed border-input bg-card/60',
                    )}
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <h4 id="sb-companions-title" className="text-base font-semibold">
                          <span aria-hidden="true" className="mr-2">🤝</span>
                          {t('storybookPage.fields.companion.title')}
                        </h4>
                        <span className="text-xs text-muted-foreground">{t('storybookPage.builder.optional')}</span>
                      </div>
                      <p id="sb-companions-help" className="mt-1 text-sm text-muted-foreground">
                        {t('storybookPage.fields.companion.help')}
                      </p>
                    </div>

                    {form.companions.map((c, index) => companionCard(c, index))}

                    {form.companions.length < maxCompanions && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={addCompanion}
                        aria-describedby="sb-companions-help"
                        className="h-auto min-h-11 w-full whitespace-normal rounded-full py-2.5 sm:w-auto"
                      >
                        <UserPlus aria-hidden="true" />
                        {form.companions.length
                          ? t('storybookPage.fields.companion.addAnother')
                          : t('storybookPage.fields.companion.add')}
                      </Button>
                    )}
                  </section>
                )}

                {petAvailable && (
                  <section
                    aria-labelledby="sb-pet-title"
                    data-testid="sb-pet"
                    className={cn(
                      'space-y-5 rounded-2xl border p-4 transition-colors sm:p-6',
                      form.petOn ? 'border-border bg-muted/40' : 'border-dashed border-input bg-card/60',
                    )}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <h4 id="sb-pet-title" className="text-base font-semibold">
                            <span aria-hidden="true" className="mr-2">🐾</span>
                            {t('storybookPage.pet.title')}
                          </h4>
                          <span className="text-xs text-muted-foreground">{t('storybookPage.builder.optional')}</span>
                        </div>
                        <p id="sb-pet-help" className="mt-1 text-sm text-muted-foreground">{t('storybookPage.pet.help')}</p>
                      </div>
                      <Switch
                        id={FIELD_IDS.petOn}
                        checked={form.petOn}
                        onCheckedChange={(checked) => update('petOn', checked)}
                        aria-labelledby="sb-pet-title"
                        aria-describedby="sb-pet-help"
                      />
                    </div>
                    {form.petOn && (
                      <div className="space-y-5">
                        <div className="grid gap-5 sm:grid-cols-2">
                          <Field id={FIELD_IDS.petName} label={t('storybookPage.pet.nameLabel')} error={err('petName')}>
                            <Input
                              id={FIELD_IDS.petName}
                              value={form.petName}
                              onChange={(e) => update('petName', e.target.value)}
                              placeholder={t('storybookPage.pet.namePlaceholder')}
                              maxLength={limits.nameMax || undefined}
                              autoComplete="off"
                              autoCapitalize="words"
                              spellCheck={false}
                              aria-invalid={invalid('petName')}
                              aria-describedby={describedBy(FIELD_IDS.petName, { error: !!err('petName') })}
                              className={cn('h-11', err('petName') && 'border-destructive')}
                            />
                          </Field>
                          <Field id={FIELD_IDS.petKind} label={t('storybookPage.pet.kindLabel')} error={err('petKind')}>
                            <select
                              id={FIELD_IDS.petKind}
                              value={form.petKind}
                              onChange={(e) => update('petKind', e.target.value)}
                              aria-invalid={invalid('petKind')}
                              aria-describedby={describedBy(FIELD_IDS.petKind, { error: !!err('petKind') })}
                              className={cn(selectClass, err('petKind') ? 'border-destructive' : 'border-input')}
                            >
                              <option value="" disabled>
                                {t('storybookPage.pet.kindPlaceholder')}
                              </option>
                              {petKinds.map((kind) => (
                                <option key={kind} value={kind}>
                                  {t(`storybookPage.pet.kinds.${appearanceSlug(kind)}`, { defaultValue: kind })}
                                </option>
                              ))}
                            </select>
                          </Field>
                        </div>
                        <Field
                          id={FIELD_IDS.petLook}
                          label={t('storybookPage.pet.lookLabel')}
                          optional
                          help={t('storybookPage.pet.lookHelp')}
                          error={err('petLook')}
                          counter={limits.detailsMax ? { count: form.petLook.length, max: limits.detailsMax } : undefined}
                        >
                          <Input
                            id={FIELD_IDS.petLook}
                            value={form.petLook}
                            onChange={(e) => update('petLook', e.target.value)}
                            placeholder={t('storybookPage.pet.lookPlaceholder')}
                            maxLength={limits.detailsMax || undefined}
                            autoComplete="off"
                            aria-invalid={invalid('petLook')}
                            aria-describedby={describedBy(FIELD_IDS.petLook, { help: true, error: !!err('petLook') })}
                            className="h-11"
                          />
                        </Field>
                      </div>
                    )}
                  </section>
                )}
              </div>
            </div>
          </div>
        )}

        {stepKey === 'photos' && (
          <div className="space-y-6">
            {grownUps.length > 0 ? (
              <StorybookAvatarPicker
                people={grownUps}
                value={form.avatars}
                onChange={setAvatar}
                signedIn={signedIn}
                onSignIn={signInForAvatar}
                crew={crewState}
                onBeforeCheckout={saveDraft}
              />
            ) : (
              companionAvailable &&
              form.companions.length < maxCompanions && (
                <section
                  aria-labelledby="sb-add-yourself-title"
                  data-testid="sb-add-yourself"
                  className="space-y-3 rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 p-4 sm:p-6"
                >
                  <h4 id="sb-add-yourself-title" className="flex items-center gap-2 text-base font-semibold">
                    <ScanFace aria-hidden="true" className="h-5 w-5 text-primary" />
                    {t('storybookPage.avatars.addYourselfTitle')}
                  </h4>
                  <p className="text-sm text-muted-foreground">{t('storybookPage.avatars.noGrownUp')}</p>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-auto min-h-11 w-full whitespace-normal rounded-full sm:w-auto"
                    onClick={() => {
                      goToStep('hero');
                      addCompanion();
                    }}
                  >
                    <UserPlus aria-hidden="true" />
                    {t('storybookPage.avatars.addMyself')}
                  </Button>
                </section>
              )
            )}
            <h4 className="flex items-center gap-2 text-base font-semibold">
              <Camera aria-hidden="true" className="h-5 w-5 text-primary" />
              {t('storybookPage.photos.uploadTitle')}
            </h4>
            <StorybookPhotoTagger
              idPrefix="sb-photos"
              people={people}
              photos={form.photos}
              onPhotosChange={setPhotos}
              limits={tagLimits}
              onBusyChange={setPhotoBusy}
              error={err('photos')}
              photoErrors={photoErrors}
              onPhotoEdited={(key) =>
                setPhotoErrors((prev) => {
                  if (!prev[key]) return prev;
                  const next = { ...prev };
                  delete next[key];
                  return next;
                })
              }
            />
            {colourPanel()}
            <p className="text-sm text-muted-foreground">{t('storybookPage.photos.skipNote')}</p>
          </div>
        )}

        {stepKey === 'story' && (
          <div className="space-y-8">
            <Field id={FIELD_IDS.themeId} group label={t('storybookPage.fields.theme.label')} error={err('themeId')}>
              <StorybookChoiceGroup
                id={FIELD_IDS.themeId}
                variant="card"
                value={form.themeId}
                onChange={(value) => update('themeId', value)}
                options={themeChoices}
                labelledBy={`${FIELD_IDS.themeId}-label`}
                describedBy={describedBy(FIELD_IDS.themeId, { error: !!err('themeId') })}
                invalid={!!err('themeId')}
              />
            </Field>

            <Field
              id={FIELD_IDS.lessonId}
              label={t('storybookPage.fields.lesson.label')}
              optional
              help={t('storybookPage.fields.lesson.help')}
            >
              <select
                id={FIELD_IDS.lessonId}
                value={form.lessonId}
                onChange={(e) => update('lessonId', e.target.value)}
                aria-describedby={describedBy(FIELD_IDS.lessonId, { help: true })}
                className={cn(selectClass, 'border-input')}
              >
                {!(config.lessons || []).some((lesson) => lesson.id === 'none') && (
                  <option value="">{t('storybookPage.fields.lesson.none')}</option>
                )}
                {(config.lessons || []).map((lesson) => (
                  <option key={lesson.id} value={lesson.id}>
                    {storybookOptionLabel(lesson, i18n.language)}
                  </option>
                ))}
              </select>
            </Field>

            <div className="grid gap-5 sm:grid-cols-2">
              <Field
                id={FIELD_IDS.interests}
                label={t('storybookPage.fields.interests.label')}
                optional
                help={t('storybookPage.fields.interests.help')}
                error={err('interests')}
                counter={limits.interestsMax ? { count: form.interests.length, max: limits.interestsMax } : undefined}
              >
                <Textarea
                  id={FIELD_IDS.interests}
                  value={form.interests}
                  onChange={(e) => update('interests', e.target.value)}
                  placeholder={t('storybookPage.fields.interests.placeholder')}
                  maxLength={limits.interestsMax || undefined}
                  rows={3}
                  aria-invalid={invalid('interests')}
                  aria-describedby={describedBy(FIELD_IDS.interests, { help: true, error: !!err('interests') })}
                  className="text-base md:text-sm"
                />
              </Field>
              <Field
                id={FIELD_IDS.sidekick}
                label={t('storybookPage.fields.sidekick.label')}
                optional
                help={t('storybookPage.fields.sidekick.help')}
                error={err('sidekick')}
                counter={limits.sidekickMax ? { count: form.sidekick.length, max: limits.sidekickMax } : undefined}
              >
                <Input
                  id={FIELD_IDS.sidekick}
                  value={form.sidekick}
                  onChange={(e) => update('sidekick', e.target.value)}
                  placeholder={t('storybookPage.fields.sidekick.placeholder')}
                  maxLength={limits.sidekickMax || undefined}
                  autoComplete="off"
                  aria-invalid={invalid('sidekick')}
                  aria-describedby={describedBy(FIELD_IDS.sidekick, { help: true, error: !!err('sidekick') })}
                  className="h-11"
                />
              </Field>
            </div>

            {/* One style offered (the 3D style, owner 2026-09-29): nothing to pick. */}
            {artStyleChoices.length > 1 && (
              <Field id={FIELD_IDS.artStyleId} group label={t('storybookPage.fields.artStyle.label')} error={err('artStyleId')}>
                <StorybookChoiceGroup
                  id={FIELD_IDS.artStyleId}
                  variant="card"
                  value={form.artStyleId}
                  onChange={(value) => update('artStyleId', value)}
                  options={artStyleChoices}
                  labelledBy={`${FIELD_IDS.artStyleId}-label`}
                  describedBy={describedBy(FIELD_IDS.artStyleId, { error: !!err('artStyleId') })}
                  invalid={!!err('artStyleId')}
                />
              </Field>
            )}

            <Field
              id={FIELD_IDS.language}
              group
              label={t('storybookPage.fields.language.label')}
              help={t('storybookPage.fields.language.help')}
            >
              <StorybookChoiceGroup
                id={FIELD_IDS.language}
                value={form.language}
                onChange={(value) => update('language', value as StorybookLanguage)}
                options={languageChoices}
                labelledBy={`${FIELD_IDS.language}-label`}
                describedBy={describedBy(FIELD_IDS.language, { help: true })}
              />
            </Field>
          </div>
        )}

        {stepKey === 'final' && (
          <div className="space-y-8">
            <div className="flex items-start gap-3 rounded-2xl border border-border bg-secondary/60 p-4 text-secondary-foreground">
              <span aria-hidden="true" className="text-3xl leading-none">{selectedTheme?.emoji || '📖'}</span>
              <div className="min-w-0 text-sm">
                <p className="font-semibold">
                  {companionNames.length
                    ? t('storybookPage.builder.recapTitleWithCompanion', {
                        name: normalizeName(form.childName),
                        companion: formatStorybookList(companionNames, i18n.language),
                      })
                    : t('storybookPage.builder.recapTitle', { name: normalizeName(form.childName) })}
                </p>
                <p className="mt-0.5 text-secondary-foreground/80">
                  {[
                    selectedTheme ? storybookOptionLabel(selectedTheme, i18n.language) : null,
                    artStyleChoices.find((style) => style.value === form.artStyleId)?.label,
                    t(`storybookPage.fields.language.${form.language}`, { defaultValue: form.language }),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
            </div>

            <Field
              id={FIELD_IDS.dedication}
              label={t('storybookPage.fields.dedication.label')}
              optional
              help={t('storybookPage.fields.dedication.help')}
              error={err('dedication')}
              counter={limits.dedicationMax ? { count: form.dedication.length, max: limits.dedicationMax } : undefined}
            >
              <Textarea
                id={FIELD_IDS.dedication}
                value={form.dedication}
                onChange={(e) => update('dedication', e.target.value)}
                placeholder={t('storybookPage.fields.dedication.placeholder')}
                maxLength={limits.dedicationMax || undefined}
                rows={3}
                aria-invalid={invalid('dedication')}
                aria-describedby={describedBy(FIELD_IDS.dedication, { help: true, error: !!err('dedication') })}
                className="text-base md:text-sm"
              />
            </Field>

            <Field
              id={FIELD_IDS.email}
              label={t('storybookPage.fields.email.label')}
              help={t('storybookPage.fields.email.help')}
              error={err('email')}
            >
              <Input
                id={FIELD_IDS.email}
                type="email"
                inputMode="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                placeholder={t('storybookPage.fields.email.placeholder')}
                required
                aria-invalid={invalid('email')}
                aria-describedby={describedBy(FIELD_IDS.email, { help: true, error: !!err('email') })}
                className={cn('h-11', err('email') && 'border-destructive')}
              />
            </Field>

            <div className="space-y-2">
              <div
                className={cn(
                  'flex items-start gap-3 rounded-2xl border bg-card p-4',
                  err('consent') ? 'border-destructive' : 'border-border',
                )}
              >
                <Checkbox
                  id={FIELD_IDS.consent}
                  checked={form.consent}
                  onCheckedChange={(checked) => update('consent', checked === true)}
                  aria-invalid={invalid('consent')}
                  aria-describedby={describedBy(FIELD_IDS.consent, { error: !!err('consent') })}
                  className="mt-0.5 h-5 w-5"
                />
                <Label htmlFor={FIELD_IDS.consent} className="cursor-pointer text-sm font-normal leading-relaxed">
                  {t('storybookPage.fields.consent.label')}{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="font-semibold text-primary underline underline-offset-2">
                    {t('storybookPage.fields.consent.terms')}
                  </a>
                  {' · '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="font-semibold text-primary underline underline-offset-2">
                    {t('storybookPage.fields.consent.privacy')}
                  </a>
                </Label>
              </div>
              {err('consent') && (
                <p id={`${FIELD_IDS.consent}-error`} className="text-sm font-medium text-destructive">
                  {err('consent')}
                </p>
              )}
            </div>

            {sendingPhotos && (
              <div className="space-y-2">
                <div
                  className={cn(
                    'flex items-start gap-3 rounded-2xl border bg-card p-4',
                    err('photoConsent') ? 'border-destructive' : 'border-border',
                  )}
                >
                  <Checkbox
                    id={FIELD_IDS.photoConsent}
                    checked={form.photoConsent}
                    onCheckedChange={(checked) => update('photoConsent', checked === true)}
                    aria-invalid={invalid('photoConsent')}
                    aria-describedby={describedBy(FIELD_IDS.photoConsent, { error: !!err('photoConsent') })}
                    className="mt-0.5 h-5 w-5"
                  />
                  <Label htmlFor={FIELD_IDS.photoConsent} className="cursor-pointer text-sm font-normal leading-relaxed">
                    {t('storybookPage.fields.photoConsent.label')}
                  </Label>
                </div>
                {err('photoConsent') && (
                  <p id={`${FIELD_IDS.photoConsent}-error`} className="text-sm font-medium text-destructive">
                    {err('photoConsent')}
                  </p>
                )}
              </div>
            )}

            <p className="flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" aria-hidden="true" />
              {t('storybookPage.builder.privacyNote')}
            </p>
          </div>
        )}

        {step === lastStep && <SiteMarkSwitch testId="storybook-site-mark" disabled={submitting} />}

        {formError && (
          <div role="alert" className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
            {formError}
          </div>
        )}

        <div className="flex flex-col-reverse gap-3 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between">
          {step > 0 ? (
            <Button type="button" variant="outline" size="lg" onClick={goBack} disabled={submitting} className="rounded-full h-auto min-h-12 whitespace-normal py-3 text-center leading-snug">
              <ArrowLeft aria-hidden="true" />
              {t('storybookPage.builder.back')}
            </Button>
          ) : (
            <span className="hidden sm:block" />
          )}
          {step < lastStep ? (
            <Button type="submit" size="lg" disabled={photoBusy} className="rounded-full h-auto min-h-12 whitespace-normal py-3 text-center leading-snug">
              {stepKey === 'photos' && !form.photos.length && !avatarCast.length
                ? t('storybookPage.photos.skip')
                : steps[step + 1] === 'photos'
                  ? t('storybookPage.builder.nextToPhotos')
                  : t('storybookPage.builder.next')}
              <ArrowRight aria-hidden="true" />
            </Button>
          ) : !online ? (
            // The one button that starts the book (owner 2026-10-16): greyed while
            // the drawing machines are offline, back by itself.
            <OfflineButton tone="light" className="sm:max-w-sm" />
          ) : (
            <Button type="submit" size="lg" disabled={submitting || photoBusy} className="rounded-full h-auto min-h-12 whitespace-normal py-3 text-center leading-snug">
              {submitting ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Sparkles aria-hidden="true" />}
              {submitting ? t('storybookPage.builder.submitting') : t(gift ? 'storybookGift.redeem.submit' : 'storybookPage.builder.submit')}
            </Button>
          )}
        </div>
        {step === lastStep && (
          <p className="-mt-4 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground sm:justify-end">
            <Lock className="h-3.5 w-3.5" aria-hidden="true" />
            {gift ? t('storybookGift.redeem.paidNote', { name: gift.fromName }) : t('storybookPage.builder.freeNote')}
          </p>
        )}
      </form>
    </div>
  );
}
